/**
 * Shared plan-data accessors for the public marketing surface.
 *
 * Single source of truth for how marketing components read the plan list.
 * Data arrives via `window._pageConfig.plans`, which is populated by the
 * Razor view from `ViewBag.PlansJson`, which is populated by HomeController
 * calling IMarketingPlanService - the service caches live Sign_SubscriptionPlans
 * reads (with stale-while-error on Platform.API failures).
 *
 * There is deliberately NO bundled static snapshot here. If the server fails
 * to populate _pageConfig.plans, marketing components must show a visible
 * error state rather than silently rendering stale/wrong data.
 */

/**
 * Returns the active plan list as provided by the server.
 * Empty array means the server had no plans available (Platform.API unreachable
 * AND cache cold). Callers MUST handle the empty case with a visible error -
 * do not silently degrade.
 */
export function getPlans() {
    const live = (typeof window !== 'undefined' && window._pageConfig && window._pageConfig.plans)
        ? window._pageConfig.plans
        : null;

    return Array.isArray(live) ? live : [];
}

/**
 * Look up a single plan by TierCode (case-insensitive).
 * Returns null if the plan list is empty OR the tierCode isn't in the list.
 * Callers must render a visible error on null - no silent fallback.
 */
export function getPlanByTierCode(tierCode) {
    if (!tierCode) return null;
    const plans = getPlans();
    if (plans.length === 0) return null;
    const code = String(tierCode).toLowerCase();
    return plans.find(p => String(p.tierCode).toLowerCase() === code) || null;
}

/**
 * Whether the server successfully populated the plan list for this page.
 * Components bind to this to flip their template between normal and
 * error-state rendering.
 */
export function plansAreAvailable() {
    return getPlans().length > 0;
}

/**
 * Friendly string for the "how many users are included / extra cost" sub-line.
 * Handles the unlimited case (userLimit === -1) and the single-seat case
 * (userLimit === 1, e.g., Developer tier).
 */
export function formatUserSubline(plan, { annual = false } = {}) {
    if (!plan) return '';
    const base = plan.baseUserCount || 1;
    const extraPrice = annual ? (plan.perUserAnnualPrice || 0) : (plan.perUserMonthlyPrice || 0);
    const cap = plan.userLimit;

    if (cap === 1) {
        return '1 seat (single developer tier)';
    }
    if (extraPrice <= 0) {
        return cap === -1 ? 'Unlimited users' : `${base} user${base === 1 ? '' : 's'}`;
    }
    const capCopy = cap === -1 ? 'unlimited' : `up to ${cap}`;
    return `${base} user${base === 1 ? '' : 's'} included · +$${extraPrice.toFixed(0)}/mo per extra (${capCopy})`;
}

/**
 * Returns the user-facing price as a dollar string (no cents when whole number).
 * '$0' / '$19' / '$14.50' - never '$19.00'.
 */
export function formatPriceDisplay(plan, { annual = false } = {}) {
    if (!plan) return '$0';
    const value = annual ? (plan.annualPrice || 0) : (plan.monthlyPrice || 0);
    if (value === 0) return '$0';
    if (value % 1 === 0) return `$${value.toFixed(0)}`;
    return `$${value.toFixed(2)}`;
}

// ─────────────────────────────────────────────────────────────────────
// Plan group metadata
// ─────────────────────────────────────────────────────────────────────
//
// Plans are classified into three semantic groups for the marketing
// surface via the `planGroup` field (stored in Sign_SubscriptionPlans):
//
//   * 'individual' - solo / ad-hoc users.
//   * 'smb'        - small business through upper-SMB.
//   * 'developer'  - ISV / API-first / agentic AI builders.
//
// This file owns the DISPLAY metadata for each group (headers, taglines,
// ordering). The DB owns membership; display is a presentation concern.

const GROUP_METADATA = {
    individual: {
        key: 'individual',
        label: 'For Individuals',
        tagline: 'One contract, one signing, no subscription.',
        icon: 'person',
        order: 0
    },
    smb: {
        key: 'smb',
        label: 'Small Business & Enterprise',
        tagline: 'Team plans from solo practices to 100-person orgs needing SSO.',
        icon: 'business',
        order: 1
    },
    developer: {
        key: 'developer',
        label: 'Developers & AI Agents',
        tagline: 'REST API, embedded signing, and MCP-native for Claude-driven flows.',
        icon: 'code',
        order: 2
    }
};

// If the DB returns a group value we don't recognize, we still surface it
// rather than silently dropping the plan. Unknown groups sort last.
const UNKNOWN_GROUP_ORDER = 999;

function resolveGroupMetadata(groupKey) {
    const key = (groupKey || '').toLowerCase();
    return GROUP_METADATA[key] || {
        key: key || 'other',
        label: key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Other plans',
        tagline: '',
        icon: 'category',
        order: UNKNOWN_GROUP_ORDER
    };
}

/**
 * Display label for a single group key. Used by wizards / result chips.
 */
export function getGroupLabel(groupKey) {
    return resolveGroupMetadata(groupKey).label;
}

/**
 * Returns the plans bucketed into their semantic groups, each bucket
 * sorted by SortOrder. Shape:
 *   [
 *     { key: 'individual', label: 'For Individuals', tagline, icon, plans: [...] },
 *     { key: 'smb', label: 'Small Business & Enterprise', plans: [...] },
 *     { key: 'developer', label: 'Developers & AI Agents', plans: [...] }
 *   ]
 * Empty groups are omitted. Returns [] when no plans are available.
 *
 * Diagnostic: if any plan arrives with no planGroup value at all, the most
 * common cause is a stale Platform.API binary that doesn't yet project the
 * PlanGroup field. We log once per page load so the footgun is obvious in
 * DevTools instead of silently collapsing all plans into the SMB bucket.
 */
let warnedAboutMissingGroup = false;
export function getPlanGroups() {
    const plans = getPlans();
    if (plans.length === 0) return [];

    // Bucket.
    const buckets = new Map();
    const missingGroup = [];
    for (const plan of plans) {
        const raw = plan.planGroup;
        if (!raw) missingGroup.push(plan.tierCode || '(unknown)');
        const key = (raw || '').toLowerCase() || 'smb';
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(plan);
    }

    if (missingGroup.length > 0 && !warnedAboutMissingGroup) {
        warnedAboutMissingGroup = true;
        console.warn(
            `[planData] ${missingGroup.length} plan(s) arrived with no planGroup - grouping defaulted to 'smb'. ` +
            `Check that Platform.API has been rebuilt/restarted so its /subscription/plans DTO projects the PlanGroup column. ` +
            `Affected tierCodes: ${missingGroup.join(', ')}`
        );
    }

    // Build the ordered array with metadata + sorted plans.
    const out = [];
    for (const [key, groupPlans] of buckets.entries()) {
        const meta = resolveGroupMetadata(key);
        const sorted = [...groupPlans].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
                || (a.monthlyPrice ?? 0) - (b.monthlyPrice ?? 0)
        );
        out.push({ ...meta, plans: sorted });
    }
    out.sort((a, b) => a.order - b.order);
    return out;
}
